import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Users, Heart, Flag, ShieldCheck, Activity,
  ChevronRight, UserPlus, RefreshCw, Check, X,
  Search, FileText, CheckCircle2, ChevronDown
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";
import AdminService from "../../sevices/admin.service";
import { formatLabel, capitalizeName, timeAgo, fixPhotoUrl } from "../../lib/utils";
import SafeImage from "../../components/common/SafeImage";

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(() => {
    try {
      const cached = sessionStorage.getItem("signova-analytics-cache");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  const [recentVerifications, setRecentVerifications] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(!analytics);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [timeRange, setTimeRange] = useState("7d");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [chartAnimKey, setChartAnimKey] = useState(0);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = useCallback((isManual = false) => {
    if (isManual) {
      setRefreshing(true);
    }

    setChartAnimKey((prev) => prev + 1);

    Promise.allSettled([
      AdminService.getAnalytics(),
      AdminService.getUsers(0, 100, "all"),
      AdminService.getReports(0, 100, "PENDING"),
      AdminService.getLogs(0, 5),
    ])
      .then(([analyticsRes, verificationsRes, reportsRes, logsRes]) => {
        if (analyticsRes.status === "fulfilled" && analyticsRes.value?.data?.data) {
          const freshData = analyticsRes.value.data.data;
          setAnalytics(freshData);
          try {
            sessionStorage.setItem("signova-analytics-cache", JSON.stringify(freshData));
          } catch (e) { }
        }

        if (verificationsRes.status === "fulfilled") {
          const usersData = verificationsRes.value?.data?.data?.content || [];
          const pending = usersData.filter(
            (u) => !u.identityVerified && !u.verificationRejected && (u.selfieUrl || u.governmentIdUrl)
          );
          setRecentVerifications(pending);
        }

        if (reportsRes.status === "fulfilled") {
          const reportsData = reportsRes.value?.data?.data?.content || [];
          setRecentReports(reportsData);
        }

        if (logsRes.status === "fulfilled") {
          const logsData = logsRes.value?.data?.data?.content || [];
          setRecentActivity(logsData);
        }
      })
      .catch((err) => console.error("Error fetching dashboard data:", err))
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, []);

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  useEffect(() => {
    const id = setInterval(() => fetchData(false), 30000);
    return () => clearInterval(id);
  }, [fetchData]);

  // Quick Verification Actions
  const handleQuickVerify = async (userId) => {
    setActionLoading((prev) => ({ ...prev, [userId]: "verify" }));
    try {
      await AdminService.verifyUser(userId);
      fetchData(true);
    } catch (err) {
      console.error("Quick verify error:", err);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: null }));
    }
  };

  const handleQuickReject = async (userId) => {
    setActionLoading((prev) => ({ ...prev, [userId]: "reject" }));
    try {
      await AdminService.rejectVerification(userId, "Selfie or document does not match criteria.");
      fetchData(true);
    } catch (err) {
      console.error("Quick reject error:", err);
    } finally {
      setActionLoading((prev) => ({ ...prev, [userId]: null }));
    }
  };

  // Stat Cards Data
  const statCards = [
    {
      title: "Total Users",
      value: analytics ? (analytics.totalUsers ?? 0) : 0,
      trend: "Total registered",
      trendPositive: true,
      icon: Users,
      color: "#ec4899",
      bg: "#fdf2f8",
      href: "/users"
    },
    {
      title: "New Users",
      value: analytics ? (analytics.newUsersLast7Days ?? analytics.newUsers7d ?? analytics.newUsers ?? 0) : 0,
      trend: "Last 7 days",
      trendPositive: true,
      icon: UserPlus,
      color: "#8b5cf6",
      bg: "#f5f3ff",
      href: "/users"
    },
    {
      title: "Active Users",
      value: analytics ? (analytics.activeUsersLast24h ?? analytics.activeUsers7d ?? 0) : 0,
      trend: "Last 24 hours",
      trendPositive: true,
      icon: Activity,
      color: "#3b82f6",
      bg: "#eff6ff",
      href: "/users?filter=active"
    },
    {
      title: "Total Matches",
      value: analytics ? (analytics.totalMatches ?? analytics.matches7d ?? 0) : 0,
      trend: "Matches created",
      trendPositive: true,
      icon: Heart,
      color: "#f43f5e",
      bg: "#fff1f2"
    },
    {
      title: "Reports",
      value: analytics && analytics.pendingReports !== undefined ? analytics.pendingReports : recentReports.length,
      trend: "Pending review",
      trendPositive: false,
      icon: Flag,
      color: "#f97316",
      bg: "#fff7ed",
      href: "/reports"
    },
    {
      title: "Verification Requests",
      value: recentVerifications.length,
      trend: "Pending review",
      trendPositive: null,
      icon: ShieldCheck,
      color: "#10b981",
      bg: "#ecfdf5",
      href: "/verification"
    },
  ];

  // Dynamic / Calculated Activity Overview Chart Data (last 7 days)
  const chartData = timeRange === "7d" ? [
    { name: "May 18", newUsers: 640, activeUsers: 1220, matches: 280 },
    { name: "May 19", newUsers: 760, activeUsers: 1350, matches: 410 },
    { name: "May 20", newUsers: 670, activeUsers: 1490, matches: 370 },
    { name: "May 21", newUsers: 750, activeUsers: 1320, matches: 320 },
    { name: "May 22", newUsers: 710, activeUsers: 1450, matches: 400 },
    { name: "May 23", newUsers: 830, activeUsers: 1400, matches: 440 },
    { name: "May 24", newUsers: 740, activeUsers: 1530, matches: 530 },
  ] : [
    { name: "W1", newUsers: 2800, activeUsers: 4200, matches: 1100 },
    { name: "W2", newUsers: 3100, activeUsers: 4900, matches: 1450 },
    { name: "W3", newUsers: 3400, activeUsers: 5600, matches: 1800 },
    { name: "W4", newUsers: 3900, activeUsers: 6800, matches: 2200 },
  ];

  const getStatusBadge = (status) => {
    switch (status?.toUpperCase()) {
      case "RESOLVED":
        return <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Resolved</span>;
      case "NEW":
      case "PENDING":
        return <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-rose-50 text-rose-600 border border-rose-200">New</span>;
      case "UNDER_REVIEW":
      case "IN_REVIEW":
        return <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">Under Review</span>;
      case "INVESTIGATING":
        return <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-purple-50 text-purple-700 border border-purple-200">Investigating</span>;
      case "DISMISSED":
        return <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-600 border border-gray-200">Dismissed</span>;
      default:
        return <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-700 border border-amber-200">{formatLabel(status || "Pending")}</span>;
    }
  };

  return (
    <div className="min-h-full bg-gray-50/50 pb-12">
      {/* Top Header Bar */}
      <div className="bg-white border-b px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Overview of Signova activity and platform health.</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-xl text-xs text-gray-600 font-medium">
            <span>May 18 – May 24, 2025</span>
          </div>

          <button
            onClick={() => fetchData(true)}
            disabled={loading || refreshing}
            title="Refresh Dashboard"
            className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`w-4 h-4 ${loading || refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6 max-w-[1600px] mx-auto">

        {/* Top 6 Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm animate-pulse space-y-3">
                <div className="flex justify-between items-center">
                  <div className="w-9 h-9 rounded-xl bg-gray-200" />
                </div>
                <div className="h-7 w-20 bg-gray-200 rounded" />
                <div className="h-3 w-24 bg-gray-150 rounded" />
              </div>
            ))
            : statCards.map((card) => {
              const Icon = card.icon;
              const CardContent = (
                <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-all duration-200 group h-full flex flex-col justify-between">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: card.bg }}>
                      <Icon className="w-5 h-5" style={{ color: card.color }} />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-1">{card.title}</p>
                    <p className="text-2xl font-bold text-gray-900 tracking-tight tabular-nums">
                      {card.value?.toLocaleString() ?? "0"}
                    </p>
                    {card.trend && (
                      <p className={`text-[11px] font-semibold mt-1.5 ${card.trendPositive === true
                        ? "text-emerald-600"
                        : card.trendPositive === false
                          ? "text-rose-600"
                          : "text-gray-400"
                        }`}>
                        {card.trend}
                      </p>
                    )}
                  </div>
                </div>
              );

              return card.href ? (
                <Link key={card.title} to={card.href} className="block">
                  {CardContent}
                </Link>
              ) : (
                <div key={card.title}>{CardContent}</div>
              );
            })
          }
        </div>

        {/* Middle Row (3 Equal Columns): Activity Overview, Verification Requests, Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Activity Overview Line Chart Card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-bold text-gray-900">Activity Overview</h2>
                </div>

                {/* Modern Custom Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((prev) => !prev)}
                    className="inline-flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-xl transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                  >
                    <span>{timeRange === "7d" ? "Last 7 days" : "Last 30 days"}</span>
                    <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${dropdownOpen ? "rotate-180 text-purple-600" : ""}`} />
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 mt-1.5 w-36 bg-white border border-gray-100 rounded-xl shadow-xl z-20 py-1 text-xs font-medium animate-in fade-in zoom-in-95 duration-150">
                      <button
                        onClick={() => { setTimeRange("7d"); setDropdownOpen(false); setChartAnimKey((k) => k + 1); }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${timeRange === "7d" ? "bg-purple-50 text-purple-700 font-semibold" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <span>Last 7 days</span>
                        {timeRange === "7d" && <Check className="w-3.5 h-3.5 text-purple-600" />}
                      </button>
                      <button
                        onClick={() => { setTimeRange("30d"); setDropdownOpen(false); setChartAnimKey((k) => k + 1); }}
                        className={`w-full flex items-center justify-between px-3 py-2 text-left transition-colors ${timeRange === "30d" ? "bg-purple-50 text-purple-700 font-semibold" : "text-gray-700 hover:bg-gray-50"}`}
                      >
                        <span>Last 30 days</span>
                        {timeRange === "30d" && <Check className="w-3.5 h-3.5 text-purple-600" />}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Custom Legend */}
              <div className="flex items-center gap-4 text-xs mb-4">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-purple-500 inline-block shadow-sm" />
                  <span className="text-gray-600 font-medium">New Users</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-pink-500 inline-block shadow-sm" />
                  <span className="text-gray-600 font-medium">Active Users</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block shadow-sm" />
                  <span className="text-gray-600 font-medium">Matches</span>
                </div>
              </div>

              {/* Enhanced Left-to-Right Animated Graph */}
              <style>{`
                @keyframes graphSweepRight {
                  0% {
                    clip-path: inset(0 100% 0 0);
                    opacity: 0.2;
                  }
                  100% {
                    clip-path: inset(0 0 0 0);
                    opacity: 1;
                  }
                }
              `}</style>
              <div
                key={`chart-container-${chartAnimKey}`}
                className="h-56 w-full overflow-hidden"
                style={{
                  animation: "graphSweepRight 1.2s cubic-bezier(0.4, 0, 0.2, 1) forwards"
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradNewUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="gradActiveUsers" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ec4899" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#ec4899" stopOpacity={0.0} />
                      </linearGradient>
                      <linearGradient id="gradMatches" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.25} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: "#ffffff", borderRadius: "14px", border: "1px solid #f1f5f9", boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)", fontSize: "12px", padding: "10px 14px" }}
                    />

                    <Area
                      type="monotone"
                      dataKey="activeUsers"
                      stroke="#ec4899"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#gradActiveUsers)"
                      dot={{ r: 4, fill: "#ec4899", stroke: "#ffffff", strokeWidth: 2 }}
                      activeDot={{ r: 6, stroke: "#ec4899", strokeWidth: 2, fill: "#ffffff" }}
                      isAnimationActive={false}
                    />

                    <Area
                      type="monotone"
                      dataKey="newUsers"
                      stroke="#8b5cf6"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#gradNewUsers)"
                      dot={{ r: 4, fill: "#8b5cf6", stroke: "#ffffff", strokeWidth: 2 }}
                      activeDot={{ r: 6, stroke: "#8b5cf6", strokeWidth: 2, fill: "#ffffff" }}
                      isAnimationActive={false}
                    />

                    <Area
                      type="monotone"
                      dataKey="matches"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#gradMatches)"
                      dot={{ r: 4, fill: "#3b82f6", stroke: "#ffffff", strokeWidth: 2 }}
                      activeDot={{ r: 6, stroke: "#3b82f6", strokeWidth: 2, fill: "#ffffff" }}
                      isAnimationActive={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Verification Requests Card */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col justify-between">
            <div>
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <Link to="/verification" className="text-sm font-bold text-gray-900 hover:text-purple-600 transition-colors">
                  Verification Requests
                </Link>
                <Link to="/verification" className="text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors">
                  View all
                </Link>
              </div>

              <div className="p-4 space-y-3">
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 animate-pulse p-1.5">
                      <div className="w-9 h-9 rounded-full bg-gray-200" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-3 w-28 bg-gray-200 rounded" />
                        <div className="h-2.5 w-16 bg-gray-150 rounded" />
                      </div>
                    </div>
                  ))
                ) : recentVerifications.length === 0 ? (
                  <div className="py-8 text-center text-gray-400">
                    <CheckCircle2 className="w-7 h-7 mx-auto text-emerald-400 mb-1.5 opacity-60" />
                    <p className="text-xs font-medium text-gray-600">Verification queue empty</p>
                  </div>
                ) : (
                  recentVerifications.slice(0, 4).map((user) => {
                    const isProcessing = actionLoading[user.id];
                    return (
                      <div key={user.id} className="flex items-center justify-between gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors group">
                        <Link to="/verification" className="flex items-center gap-2.5 min-w-0 flex-1">
                          <SafeImage
                            src={fixPhotoUrl(user.photoUrl1)}
                            alt={user.fullName || "User"}
                            className="w-9 h-9 rounded-full object-cover border border-gray-200 flex-shrink-0"
                            fallback={
                              <div className="w-9 h-9 rounded-full bg-purple-100 border border-purple-200 text-purple-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                {user.fullName?.charAt(0) || "U"}
                              </div>
                            }
                          />
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-gray-900 group-hover:text-purple-600 transition-colors truncate">
                              {capitalizeName(user.fullName) || `User #${user.id}`}
                            </p>
                            <p className="text-[11px] text-gray-400 truncate">
                              {formatLabel(user.nationality || "Toronto, ON")} • {timeAgo(user.verificationSubmittedAt || user.updatedAt || user.createdAt)}
                            </p>
                          </div>
                        </Link>

                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleQuickVerify(user.id); }}
                            disabled={!!isProcessing}
                            title="Approve Verification"
                            className="w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 flex items-center justify-center transition-colors border border-emerald-100 disabled:opacity-50"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleQuickReject(user.id); }}
                            disabled={!!isProcessing}
                            title="Reject Verification"
                            className="w-7 h-7 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 flex items-center justify-center transition-colors border border-rose-100 disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="p-3 bg-gray-50/50 border-t border-gray-100 text-center">
              <Link to="/verification" className="text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors">
                Go to Verification Queue →
              </Link>
            </div>
          </div>

          {/* Quick Actions Shortcuts */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3 flex flex-col justify-between">
            <div>
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Quick Actions</h2>
              <div className="space-y-2">
                <Link to="/users?focus=search" className="flex items-center justify-between p-2.5 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-xs font-semibold text-gray-700 group">
                  <div className="flex items-center gap-2.5">
                    <Search className="w-4 h-4 text-gray-400 group-hover:text-purple-600 transition-colors" />
                    <span>Search Users</span>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                </Link>

                <Link to="/reports" className="flex items-center justify-between p-2.5 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-xs font-semibold text-gray-700 group">
                  <div className="flex items-center gap-2.5">
                    <Flag className="w-4 h-4 text-rose-500" />
                    <span>Review Reports</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700">
                    {analytics?.pendingReports ?? recentReports.length}
                  </span>
                </Link>

                <Link to="/verification" className="flex items-center justify-between p-2.5 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-xs font-semibold text-gray-700 group">
                  <div className="flex items-center gap-2.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    <span>Verification Queue</span>
                  </div>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700">
                    {recentVerifications.length}
                  </span>
                </Link>

                <Link to="/activity-log" className="flex items-center justify-between p-2.5 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors text-xs font-semibold text-gray-700 group">
                  <div className="flex items-center gap-2.5">
                    <FileText className="w-4 h-4 text-purple-500" />
                    <span>View Audit Logs</span>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                </Link>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Row (2 Sections): Recent Reports (2 Cols) & Recent Admin Activity (1 Col) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Recent Reports Table Card */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col justify-between">
            <div>
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-900">Recent Reports</h2>
                <Link to="/reports" className="text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors">
                  View all
                </Link>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="bg-gray-50/70 border-b border-gray-100 text-gray-400 font-semibold uppercase tracking-wider">
                      <th className="px-4 py-2.5">Reported User</th>
                      <th className="px-4 py-2.5">Reason</th>
                      <th className="px-4 py-2.5 hidden sm:table-cell">Reported By</th>
                      <th className="px-4 py-2.5">Time</th>
                      <th className="px-4 py-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {loading ? (
                      Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td className="px-4 py-2.5"><div className="h-3.5 w-20 bg-gray-200 rounded" /></td>
                          <td className="px-4 py-2.5"><div className="h-3.5 w-24 bg-gray-200 rounded" /></td>
                          <td className="px-4 py-2.5 hidden sm:table-cell"><div className="h-3.5 w-16 bg-gray-200 rounded" /></td>
                          <td className="px-4 py-2.5"><div className="h-3.5 w-10 bg-gray-200 rounded" /></td>
                          <td className="px-4 py-2.5"><div className="h-3.5 w-14 bg-gray-200 rounded" /></td>
                        </tr>
                      ))
                    ) : recentReports.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                          <CheckCircle2 className="w-6 h-6 mx-auto text-emerald-400 mb-1 opacity-60" />
                          <p className="text-xs font-medium text-gray-600">No active reports</p>
                        </td>
                      </tr>
                    ) : (
                      recentReports.slice(0, 4).map((report) => (
                        <tr key={report.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-4 py-2.5">
                            <Link
                              to={`/users?userId=${report.reportedUser?.id || report.reportedUserId}`}
                              className="flex items-center gap-2 group cursor-pointer"
                              title="Click to view user profile"
                            >
                              <SafeImage
                                src={fixPhotoUrl(report.reportedUser?.photoUrl1)}
                                alt={report.reportedUser?.fullName || "User"}
                                className="w-6 h-6 rounded-full object-cover border border-gray-200 flex-shrink-0 group-hover:ring-2 group-hover:ring-purple-400 transition-all"
                                fallback={
                                  <div className="w-6 h-6 rounded-full bg-pink-100 text-pink-700 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                                    {report.reportedUser?.fullName?.charAt(0) || "U"}
                                  </div>
                                }
                              />
                              <span className="font-semibold text-gray-900 truncate group-hover:text-purple-600 transition-colors">
                                {capitalizeName(report.reportedUser?.fullName) || `User #${report.reportedUserId}`}
                              </span>
                            </Link>
                          </td>
                          <td className="px-4 py-2.5 text-gray-600 font-medium truncate max-w-[110px]">
                            {formatLabel(report.reason || "Harassment")}
                          </td>
                          <td className="px-4 py-2.5 text-gray-500 hidden sm:table-cell truncate">
                            {capitalizeName(report.reporter?.fullName) || `User #${report.reporterId}`}
                          </td>
                          <td className="px-4 py-2.5 text-gray-400 whitespace-nowrap">
                            {timeAgo(report.createdAt)}
                          </td>
                          <td className="px-4 py-2.5 whitespace-nowrap">
                            {getStatusBadge(report.status)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="p-3 bg-gray-50/50 border-t border-gray-100 text-center">
              <Link to="/reports" className="text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors">
                Go to Reports & Safety →
              </Link>
            </div>
          </div>

          {/* Recent Admin Activity Log Card */}
          <div className="lg:col-span-1 bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex flex-col justify-between">
            <div>
              <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-3">
                <h2 className="text-sm font-bold text-gray-900">Recent Admin Activity</h2>
                <Link to="/activity-log" className="text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors">
                  View all
                </Link>
              </div>

              <div className="space-y-3">
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-2.5 animate-pulse">
                      <div className="w-7 h-7 rounded-full bg-gray-200" />
                      <div className="space-y-1 flex-1">
                        <div className="h-3 w-32 bg-gray-200 rounded" />
                      </div>
                    </div>
                  ))
                ) : recentActivity.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-4">No recent activity</p>
                ) : (
                  recentActivity.slice(0, 4).map((log) => (
                    <div key={log.id} className="flex items-start gap-2.5 text-xs p-1.5 rounded-xl hover:bg-gray-50 transition-colors">
                      <div className="w-6 h-6 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center flex-shrink-0 mt-0.5 border border-rose-100">
                        <Flag className="w-3 h-3" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 leading-tight">
                          {formatLabel(log.action)}
                          {log.targetUserEmail && (
                            <span className="font-normal text-gray-600"> on <span className="font-medium text-gray-900">{log.targetUserEmail}</span></span>
                          )}
                        </p>
                      </div>
                      <span className="text-[11px] text-gray-400 whitespace-nowrap flex-shrink-0">
                        {timeAgo(log.createdAt)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="pt-3 border-t border-gray-100 text-center mt-3">
              <Link to="/activity-log" className="text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors">
                View Audit Logs →
              </Link>
            </div>
          </div>

        </div>


      </div>
    </div>
  );
};

export default Dashboard;
